/**
 * Smart, Arabic-aware fuzzy search for the catalog.
 *
 * Scoring tiers (high → low):
 *   1000  exact match on a normalized title / slug
 *    700  title starts with the query
 *    500  any word in title starts with the query
 *    300  title contains the query as substring
 *    150  all query chars appear in order (subsequence)
 *    100  small Levenshtein distance (typo tolerant) to a title/word
 *      0  no match
 *
 * Short queries (1 char) only return prefix matches to avoid noise.
 * Query of 2+ chars uses the full ladder above.
 */

export type SearchableItem = {
  id: string;
  slug: string;
  title: string;
  titleEn: string;
  titleFr?: string;
  type: "movie" | "series";
  year?: number;
  poster?: string;
};

/** Strip Arabic diacritics, unify alef/yaa/taa-marbuta, drop tatweel. */
function normalizeArabic(s: string): string {
  return s
    .replace(/[\u064B-\u065F\u0670\u06D6-\u06ED]/g, "") // tashkeel
    .replace(/\u0640/g, "")                              // tatweel
    .replace(/[\u0622\u0623\u0625]/g, "\u0627")          // أإآ → ا
    .replace(/\u0649/g, "\u064A")                        // ى → ي
    .replace(/\u0629/g, "\u0647")                        // ة → ه
    .replace(/\u0624/g, "\u0648")                        // ؤ → و
    .replace(/\u0626/g, "\u064A");                       // ئ → ي
}

export function normalize(s: string): string {
  return normalizeArabic(
    s
      .toLowerCase()
      .normalize("NFKD")
      .replace(/[\u0300-\u036f]/g, "") // Latin diacritics
      .replace(/[-_/.,:;!?'"`(){}\[\]]+/g, " ")
      .replace(/\s+/g, " ")
      .trim(),
  );
}

/** Tiny Levenshtein with early exit when distance exceeds `max`. */
function levenshtein(a: string, b: string, max: number): number {
  if (a === b) return 0;
  if (Math.abs(a.length - b.length) > max) return max + 1;
  const al = a.length;
  const bl = b.length;
  if (!al) return bl;
  if (!bl) return al;
  let prev = new Array(bl + 1);
  let curr = new Array(bl + 1);
  for (let j = 0; j <= bl; j++) prev[j] = j;
  for (let i = 1; i <= al; i++) {
    curr[0] = i;
    let rowMin = curr[0];
    for (let j = 1; j <= bl; j++) {
      const cost = a.charCodeAt(i - 1) === b.charCodeAt(j - 1) ? 0 : 1;
      curr[j] = Math.min(curr[j - 1] + 1, prev[j] + 1, prev[j - 1] + cost);
      if (curr[j] < rowMin) rowMin = curr[j];
    }
    if (rowMin > max) return max + 1;
    [prev, curr] = [curr, prev];
  }
  return prev[bl];
}

function isSubsequence(q: string, s: string): boolean {
  let i = 0;
  for (let j = 0; j < s.length && i < q.length; j++) {
    if (q.charCodeAt(i) === s.charCodeAt(j)) i++;
  }
  return i === q.length;
}

function scoreCandidate(query: string, fields: string[]): number {
  if (!query) return 0;
  let best = 0;
  const qShort = query.length <= 2;

  for (const raw of fields) {
    if (!raw) continue;
    const f = normalize(raw);
    if (!f) continue;

    if (f === query) { best = Math.max(best, 1000); continue; }
    if (f.startsWith(query)) { best = Math.max(best, 700 - Math.min(f.length - query.length, 50)); continue; }

    const words = f.split(" ");
    if (words.some((w) => w.startsWith(query))) {
      best = Math.max(best, 500);
      continue;
    }
    if (qShort) continue; // very short queries: prefix only

    if (f.includes(query)) { best = Math.max(best, 300); continue; }
    if (isSubsequence(query, f)) { best = Math.max(best, 150); continue; }

    // Typo tolerance: distance ≤ ceil(query.length/4) (min 1, max 3)
    const tol = Math.min(3, Math.max(1, Math.ceil(query.length / 4)));
    if (levenshtein(query, f, tol) <= tol) { best = Math.max(best, 120); continue; }
    if (words.some((w) => w.length >= 3 && levenshtein(query, w, tol) <= tol)) {
      best = Math.max(best, 100);
    }
  }
  return best;
}

export type SearchResult<T extends SearchableItem> = { item: T; score: number };

export function smartSearch<T extends SearchableItem>(
  items: T[],
  rawQuery: string,
  limit = 8,
): SearchResult<T>[] {
  const q = normalize(rawQuery);
  if (!q) return [];
  const out: SearchResult<T>[] = [];
  for (const it of items) {
    const score = scoreCandidate(q, [it.title, it.titleEn, it.titleFr || "", it.slug]);
    if (score > 0) out.push({ item: it, score });
  }
  out.sort((a, b) => b.score - a.score || (b.item.year ?? 0) - (a.item.year ?? 0));
  return out.slice(0, limit);
}
